/**
 * Google Maps / Earth-style touch gestures for moving through the world:
 *
 * - one-finger drag  → look around (swipe to turn)
 * - two-finger drag  → pan: strafe sideways / walk forward-back (the world
 *                      follows your fingers, like dragging a map)
 * - pinch out / in   → walk forward / backward (like zooming toward the ground)
 *
 * Gestures produce *positional* movement (meters per pixel), not velocity, so
 * the world tracks the fingers directly — the thing that makes Maps feel good.
 * Spraying and jumping get dedicated on-screen buttons (see TouchButtons),
 * since a drag already means "look".
 */

export interface TouchGestureCallbacks {
  /** Whether gameplay should currently receive gestures (no menus open). */
  isActive(): boolean;
  /** Pixel-space look deltas, same semantics as mouse movementX/Y. */
  onLook(dx: number, dy: number): void;
  /** Movement in meters, in the camera-yaw frame (+strafe = right, +forward = ahead). */
  onMove(strafe: number, forward: number): void;
}

/** Meters of movement per pixel of two-finger pan. */
const PAN_METERS_PER_PX = 0.02;
/** Meters of movement per pixel of pinch spread. */
const PINCH_METERS_PER_PX = 0.025;
/** Extra gain on look deltas (finger travel is smaller than mouse travel). */
const LOOK_GAIN = 1.6;

export class TouchControls {
  private touches = new Map<number, { x: number; y: number }>();
  private lastCentroid: { x: number; y: number } | null = null;
  private lastPinchDist: number | null = null;

  constructor(
    canvas: HTMLElement,
    private readonly cb: TouchGestureCallbacks,
  ) {
    // Listeners live on the canvas only, so touches on HUD/menu elements are
    // never interpreted as gestures.
    canvas.addEventListener('touchstart', this.onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });
    canvas.addEventListener('touchend', this.onTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', this.onTouchEnd, { passive: false });
  }

  private onTouchStart = (e: TouchEvent): void => {
    e.preventDefault();
    for (const t of Array.from(e.changedTouches)) {
      this.touches.set(t.identifier, { x: t.clientX, y: t.clientY });
    }
    this.resetGestureBaseline();
  };

  private onTouchEnd = (e: TouchEvent): void => {
    e.preventDefault();
    for (const t of Array.from(e.changedTouches)) this.touches.delete(t.identifier);
    this.resetGestureBaseline();
  };

  private onTouchMove = (e: TouchEvent): void => {
    e.preventDefault();
    if (!this.cb.isActive()) {
      // Still track positions so a gesture doesn't jump when menus close.
      for (const t of Array.from(e.changedTouches)) {
        const prev = this.touches.get(t.identifier);
        if (prev) {
          prev.x = t.clientX;
          prev.y = t.clientY;
        }
      }
      this.resetGestureBaseline();
      return;
    }

    if (this.touches.size === 1) {
      const t = e.changedTouches[0];
      if (!t) return;
      const prev = this.touches.get(t.identifier);
      if (!prev) return;
      this.cb.onLook((t.clientX - prev.x) * LOOK_GAIN, (t.clientY - prev.y) * LOOK_GAIN);
      prev.x = t.clientX;
      prev.y = t.clientY;
      return;
    }

    // Two or more fingers: pan + pinch (extra fingers beyond two are ignored).
    for (const t of Array.from(e.changedTouches)) {
      const prev = this.touches.get(t.identifier);
      if (prev) {
        prev.x = t.clientX;
        prev.y = t.clientY;
      }
    }
    const pts = Array.from(this.touches.values()).slice(0, 2);
    if (pts.length < 2) return;
    const centroid = { x: (pts[0]!.x + pts[1]!.x) / 2, y: (pts[0]!.y + pts[1]!.y) / 2 };
    const pinchDist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);

    if (this.lastCentroid && this.lastPinchDist !== null) {
      // Map-style pan: the world follows the fingers. Dragging left moves the
      // scene left (you step right); dragging down pulls the scene toward you
      // (you step back).
      const strafe = -(centroid.x - this.lastCentroid.x) * PAN_METERS_PER_PX;
      let forward = (this.lastCentroid.y - centroid.y) * PAN_METERS_PER_PX;
      // Pinch out = walk forward (zoom in), pinch in = walk back.
      forward += (pinchDist - this.lastPinchDist) * PINCH_METERS_PER_PX;
      this.cb.onMove(strafe, forward);
    }
    this.lastCentroid = centroid;
    this.lastPinchDist = pinchDist;
  };

  private resetGestureBaseline(): void {
    const pts = Array.from(this.touches.values()).slice(0, 2);
    if (pts.length === 2) {
      this.lastCentroid = { x: (pts[0]!.x + pts[1]!.x) / 2, y: (pts[0]!.y + pts[1]!.y) / 2 };
      this.lastPinchDist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
    } else {
      this.lastCentroid = null;
      this.lastPinchDist = null;
    }
  }
}
